import React, { useState, useEffect, useCallback } from 'react';
import s from './layerModal.module.css';
import { wardrobeAxiosInstance, clothesAxiosInstance} from "../../../api";
import { Loader } from "../../../components/Loader/loader";

export type Layer =
    | 'ABOVE_THE_WAIST_FIRST_LAYER'
    | 'ABOVE_THE_WAIST_SECOND_LAYER'
    | 'OUTERWEAR'
    | 'PANTS'
    | 'SHOES'
    | 'BAG'
    | 'ACCESSORIES';

export interface ClothingItem {
    id: number;
    name: string;
    brand: string;
    image?: string;
    layer?: Layer;
    source?: 'closet' | 'wishlist';
    style?: string;
    colorHex?: string;
    thumbUrl?: string;
    photoUrl?: string;
    cost?: number;
    manufacturer?: string;
}

interface LayerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    availableLayers: Layer[];
    selectedLayer: Layer | null;
    selectedItem: ClothingItem | null;
    onLayerSelect: (layer: Layer | null) => void;
    onItemSelect: (item: ClothingItem | null) => void;
    onConfirm: () => void;
    layerLabels: Record<Layer, string>;
    gender?: string;
    userId?: number;
}

interface WardrobeItem {
    id: number;
    name: string;
    manufacturer: string;
    category: string;
    photoUrl: string;
    thumbUrl: string;
    cost: number;
    group: string;
    clothLayer: string;
    elementName: string;
    colorCloth: string | null;
}

interface WardrobeResponse {
    countRecords: number;
    items: WardrobeItem[];
}

interface SearchResultItem {
    id: number;
    name: string;
    photoUrl: string;
    clothLayer: string;
    elementName?: string;
    isInCloset?: boolean;
    isInWishlist?: boolean;
    cost: number;
    manufacturer?: string;
    colorCloth?: string | null;
}

interface FindClothesResponse {
    clothes: Array<{
        id: string;
        name: string;
        manufacturer: string;
        vendorCode: string;
        cost: number;
        description: string | null;
        composition: string | null;
        stylistDescription: string | null;
        photoLinks: Array<{
            thumbnail: string;
            original: string;
            photoId: string;
            isApproved: boolean | null;
            isCollagePhoto: boolean | null;
            isShopPhoto: boolean | null;
            objectStorageOriginal: string;
            objectStorageThumbnail: string;
            shopIndex: number | null;
        }>;
        detailsUrl: string | null;
        categoryName: string | null;
        groupName: string | null;
        collagePhotoLinks: any;
        elementName: string | null;
        gender: string;
        colorCloth: string | null;
        clothLayers: any;
        clothSeasons: any;
    }>;
    countClothes: number;
}

const FILTER_FIELDS = [
    { key: 'element', label: 'Элемент' },
    { key: 'color', label: 'Цвет' },
    { key: 'print', label: 'Принт' },
    { key: 'material', label: 'Материал' },
    { key: 'texture', label: 'Фактура' },
    { key: 'pattern', label: 'Текстура' },
    { key: 'cut', label: 'Крой' },
    { key: 'style', label: 'Фасон' },
    { key: 'details', label: 'Детали' }
];

interface FilterValues {
    [key: string]: string;
}

export const LayerModal: React.FC<LayerModalProps> = ({
                                                          open,
                                                          onOpenChange,
                                                          availableLayers,
                                                          selectedLayer,
                                                          selectedItem,
                                                          onLayerSelect,
                                                          onItemSelect,
                                                          onConfirm,
                                                          layerLabels,
                                                          gender,
                                                          userId,
                                                      }) => {
    const [step, setStep] = useState<'layer' | 'item'>('layer');
    const [items, setItems] = useState<ClothingItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [filters, setFilters] = useState<FilterValues>({});
    const [showFilters, setShowFilters] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<ClothingItem[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);

    const fetchItemsByLayer = useCallback(async (layer: Layer) => {
        if (!userId) {
            setItems([]);
            return;
        }

        setLoading(true);
        setError(null);
        setShowSearchResults(false);
        setSearchResults([]);

        try {
            const [closetResponse, wishlistResponse] = await Promise.all([
                wardrobeAxiosInstance.get<WardrobeResponse>(
                    'mobile/wardrobe/clothes/list',
                    {
                        params: {
                            layerName: layer,
                            userId: userId,
                        }
                    }
                ),
                wardrobeAxiosInstance.get<WardrobeResponse>(
                    'mobile/favorites/clothes',
                    {
                        params: {
                            layerName: layer,
                            userId: userId,
                        }
                    }
                )
            ]);

            const closetItems: ClothingItem[] = (closetResponse.data.items || []).map(item => ({
                id: item.id,
                name: item.name,
                brand: item.manufacturer || '',
                image: item.thumbUrl || item.photoUrl,
                thumbUrl: item.thumbUrl,
                photoUrl: item.photoUrl,
                layer: item.clothLayer as Layer,
                source: 'closet' as const,
                cost: item.cost,
                manufacturer: item.manufacturer,
                colorHex: item.colorCloth || undefined,
            }));

            const wishlistItems: ClothingItem[] = (wishlistResponse.data.items || []).map(item => ({
                id: item.id,
                name: item.name,
                brand: item.manufacturer || '',
                image: item.thumbUrl || item.photoUrl,
                thumbUrl: item.thumbUrl,
                photoUrl: item.photoUrl,
                layer: item.clothLayer as Layer,
                source: 'wishlist' as const,
                cost: item.cost,
                manufacturer: item.manufacturer,
                colorHex: item.colorCloth || undefined,
            }));

            const itemsMap = new Map<number, ClothingItem>();

            wishlistItems.forEach(item => {
                itemsMap.set(item.id, { ...item, source: 'wishlist' });
            });

            closetItems.forEach(item => {
                if (!itemsMap.has(item.id)) {
                    itemsMap.set(item.id, { ...item, source: 'closet' });
                }
            });

            const mergedItems = Array.from(itemsMap.values());
            setItems(mergedItems);
            setFilters({});
            setShowFilters(false);

        } catch (err) {
            setError('Ошибка при загрузке вещей');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const findClothesByFilters = useCallback(async () => {
        if (!selectedLayer) {
            setError('Выберите слой одежды');
            return;
        }

        const hasAnyData = Object.values(filters).some(value => value?.trim() !== '');
        if (!hasAnyData) {
            setError('Заполните хотя бы одно поле фильтра');
            return;
        }

        setIsSearching(true);
        setError(null);
        setShowSearchResults(false);
        setSearchResults([]);

        try {
            const characteristics = [];

            if (filters.element?.trim()) {
                characteristics.push({
                    charName: 'элемент',
                    charValues: filters.element.trim()
                });
            }

            FILTER_FIELDS.forEach(field => {
                if (field.key !== 'element' && filters[field.key]?.trim()) {
                    characteristics.push({
                        charName: field.label.toLowerCase(),
                        charValues: filters[field.key].trim()
                    });
                }
            });

            const requestBody = {
                characteristics: characteristics,
                layer: selectedLayer,
                gender: gender || 'male'
            };

            const response = await clothesAxiosInstance.post<FindClothesResponse>(
                'findClothesInPersonalCapsule',
                requestBody
            );

            const results: ClothingItem[] = (response.data.clothes || []).map(item => {
                const inCloset = items.some(i => i.id === parseInt(item.id) && i.source === 'closet');
                const inWishlist = items.some(i => i.id === parseInt(item.id) && i.source === 'wishlist');

                let source: 'closet' | 'wishlist' | undefined = undefined;
                if (inWishlist) source = 'wishlist';
                else if (inCloset) source = 'closet';

                const photoUrl = item.photoLinks && item.photoLinks.length > 0
                    ? item.photoLinks[0].original
                    : undefined;
                const thumbUrl = item.photoLinks && item.photoLinks.length > 0
                    ? item.photoLinks[0].thumbnail
                    : undefined;

                return {
                    id: parseInt(item.id),
                    name: item.name,
                    brand: item.manufacturer || '',
                    image: thumbUrl || photoUrl,
                    thumbUrl: thumbUrl,
                    photoUrl: photoUrl,
                    layer: selectedLayer,
                    source: source,
                    cost: item.cost,
                    manufacturer: item.manufacturer,
                    colorHex: item.colorCloth || undefined,
                };
            });

            setSearchResults(results);
            setShowSearchResults(true);

            if (results.length === 0) {
                setError('По вашему запросу ничего не найдено');
            }

        } catch (err) {
            setError('Ошибка при поиске одежды');
        } finally {
            setIsSearching(false);
        }
    }, [selectedLayer, filters, items, gender]);

    const resetFilters = useCallback(() => {
        setFilters({});
        setSearchResults([]);
        setShowSearchResults(false);
        setError(null);
        if (selectedLayer) {
            fetchItemsByLayer(selectedLayer);
        }
    }, [selectedLayer, fetchItemsByLayer]);

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setShowSearchResults(false);
        setSearchResults([]);
    };

    useEffect(() => {
        if (step === 'item' && selectedLayer) {
            fetchItemsByLayer(selectedLayer);
        }
    }, [step, selectedLayer, fetchItemsByLayer]);

    const handleLayerClick = (layer: Layer) => {
        onLayerSelect(layer);
        setStep('item');
        setItems([]);
        setSearchQuery('');
        setError(null);
        setFilters({});
        setShowFilters(false);
        setShowSearchResults(false);
        setSearchResults([]);
        fetchItemsByLayer(layer);
    };

    const handleBack = () => {
        setStep('layer');
        onLayerSelect(null);
        onItemSelect(null);
        setItems([]);
        setSearchQuery('');
        setError(null);
        setFilters({});
        setShowFilters(false);
        setShowSearchResults(false);
        setSearchResults([]);
    };

    const handleItemClick = (item: ClothingItem) => {
        if (selectedItem?.id === item.id) {
            onItemSelect(null);
        } else {
            onItemSelect(item);
        }
    };

    const handleConfirmClick = () => {
        if (selectedLayer && selectedItem) {
            onConfirm();
            setStep('layer');
            onLayerSelect(null);
            onItemSelect(null);
            setItems([]);
        }
    };

    const handleClose = () => {
        setStep('layer');
        setItems([]);
        setSearchQuery('');
        setError(null);
        setFilters({});
        setShowFilters(false);
        setShowSearchResults(false);
        setSearchResults([]);
        onOpenChange(false);
    };

    const getLayerLabel = (layer: Layer): string => {
        return layerLabels[layer] || layer;
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.brand.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const renderItemsGrid = (itemsList: ClothingItem[]) => {
        if (itemsList.length === 0) return null;

        return (
            <div>
                <div className={s.sourceHeader}>
                    <span className={s.sourceCount}>
                        {itemsList.length} вещей
                    </span>
                </div>
                <div className={s.clothesGrid}>
                    {itemsList.map((item) => (
                        <div
                            key={item.id}
                            className={`${s.clothesCard} ${selectedItem?.id === item.id ? s.selected : ''}`}
                            onClick={() => handleItemClick(item)}
                        >
                            <div className={s.clothesImageWrapper}>
                                <img
                                    src={item.image || '/placeholder.svg'}
                                    alt={item.name}
                                    className={s.clothesImage}
                                />
                                {item.source === 'closet' || item.source === 'wishlist' && (
                                    <span className={s.closetBadge}>👕</span>
                                )}
                            </div>
                            <div className={s.clothesInfo}>
                                <p className={s.clothesName}>{item.name}</p>
                                <p className={s.clothesBrand}>{item.brand}</p>
                                {item.cost && (
                                    <p className={s.clothesPrice}>{item.cost.toLocaleString()} ₽</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderSearchResults = () => {
        if (!showSearchResults) return null;

        return (
            <div className={s.searchResultsSection}>
                <div className={s.searchResultsHeader}>
                    <h4>Результаты поиска</h4>
                    <span className={s.searchResultsCount}>
                        {searchResults.length} вещей
                    </span>
                    <button
                        className={s.closeSearchResultsBtn}
                        onClick={() => {
                            setShowSearchResults(false);
                            setSearchResults([]);
                        }}
                    >
                        ✕
                    </button>
                </div>
                <div className={s.clothesGrid}>
                    {searchResults.map((item) => (
                        <div
                            key={item.id}
                            className={`${s.clothesCard} ${selectedItem?.id === item.id ? s.selected : ''}`}
                            onClick={() => handleItemClick(item)}
                        >
                            <div className={s.clothesImageWrapper}>
                                <img
                                    src={item.image || '/placeholder.svg'}
                                    alt={item.name}
                                    className={s.clothesImage}
                                />
                                {item.source && (
                                    <span className={`${s.sourceBadge} ${item.source === 'closet' ? s.sourceCloset : s.sourceWishlist}`}>
                                        {item.source === 'closet' ? 'Шкаф' : 'Вишлист'}
                                    </span>
                                )}
                            </div>
                            <div className={s.clothesInfo}>
                                <p className={s.clothesName}>{item.name}</p>
                                {item.cost && (
                                    <p className={s.clothesPrice}>{item.cost.toLocaleString()} ₽</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderFilters = () => {
        if (!showFilters) return null;

        return (
            <div className={s.filtersPanel}>
                <div className={s.filtersHeader}>
                    <h4>Фильтры</h4>
                    <button
                        className={s.closeFiltersBtn}
                        onClick={() => setShowFilters(false)}
                    >
                        ✕
                    </button>
                </div>
                <div className={s.filtersGrid}>
                    {FILTER_FIELDS.map((field) => (
                        <div key={field.key} className={s.filterItem}>
                            <label className={s.filterLabel}>{field.label}:</label>
                            <input
                                type="text"
                                className={s.filterInput}
                                value={filters[field.key] || ''}
                                onChange={(e) => handleFilterChange(field.key, e.target.value)}
                                placeholder={`Введите ${field.label.toLowerCase()}`}
                            />
                        </div>
                    ))}
                </div>
                <div className={s.filtersActions}>
                    <button
                        className={s.applyFiltersBtn}
                        onClick={findClothesByFilters}
                        disabled={isSearching}
                    >
                        {isSearching ? 'Поиск...' : 'Найти по фильтрам'}
                    </button>
                    <button
                        className={s.resetFiltersBtn}
                        onClick={resetFilters}
                    >
                        Сбросить
                    </button>
                </div>
            </div>
        );
    };

    if (!open) return null;

    return (
        <div className={s.overlay} onClick={handleClose}>
            <div className={s.modal} onClick={(e) => e.stopPropagation()}>
                <div className={s.header}>
                    <h2>
                        {step === 'layer' ? 'Выберите слой' : getLayerLabel(selectedLayer!)}
                    </h2>
                    <button className={s.closeBtn} onClick={handleClose}>✕</button>
                </div>

                <div className={s.body}>
                    {step === 'layer' ? (
                        <div className={s.layerGrid}>
                            {availableLayers.map((layer) => (
                                <button
                                    key={layer}
                                    className={s.layerBtn}
                                    onClick={() => handleLayerClick(layer)}
                                >
                                    {getLayerLabel(layer)}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div>
                            <div className={s.topBar}>
                                <button className={s.backBtn} onClick={handleBack}>
                                    ← Назад к слоям
                                </button>
                                <button
                                    className={s.filterToggleBtn}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    {showFilters ? 'Скрыть фильтры' : '🔍 Фильтры'}
                                </button>
                            </div>

                            {renderFilters()}

                            {(loading || isSearching) ? (
                                <div className={s.loading}>
                                    <Loader />
                                </div>
                            ) : error ? (
                                <div className={s.errorText}>{error}</div>
                            ) : showSearchResults ? (
                                renderSearchResults()
                            ) : (
                                <>
                                    {items.length === 0 && !isSearching ? (
                                        <div className={s.emptyText}>
                                            <p>Нет вещей в этом слое</p>
                                            <p className={s.emptyHint}>
                                                {selectedLayer && `Нет вещей для слоя "${getLayerLabel(selectedLayer)}"`}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className={s.itemsContainer}>
                                            {renderItemsGrid(filteredItems)}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {step === 'item' && selectedItem && (
                    <div className={s.footer}>
                        <button className={s.confirmBtn} onClick={handleConfirmClick}>
                            Добавить в капсулу
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};